import Aurora from 'assets/collect/aurora.svg'
import MySQL from 'assets/collect/mysql.svg'
import Oracle from 'assets/collect/oracle.svg'
import PostgreSQL from 'assets/collect/postgresql.svg'
import SQLServer from 'assets/collect/microsoft.svg'

import { AuthParam, Source } from 'types/GlobalSource'
import { ESourceCategory } from 'utils/sources'

/**
 * Public data sources everyone gets — they are not something a customer
 * connects, so they are hidden from the picker.
 */
export const SOURCES_TO_SKIP: Record<string, boolean> = {
  'Boverket klimatdatabas': true,
  Brottsplatskartan: true,
  'SCB DeSO': true,
  'SCB RegSO': true,
  SGU: true,
  SMHI: true,
  Skatteverket: true,
  Valmyndigheten: true
}

export type TDatabaseCard = {
  /** The value sent as the database_type auth param. */
  database: string
  imgSrc: string
  title: string
}

/**
 * Every database goes through the single OnPremiseDirect source; the card only
 * decides which engine it connects to. Note that Oracle is deliberately mapped
 * to the postgresql driver.
 */
export const DATABASE_CARDS: TDatabaseCard[] = [
  { database: 'mysql', imgSrc: MySQL, title: 'MySQL' },
  { database: 'mysql', imgSrc: Aurora, title: 'Aurora' },
  { database: 'postgresql', imgSrc: PostgreSQL, title: 'PostgreSQL' },
  { database: 'postgresql', imgSrc: Oracle, title: 'Oracle' },
  { database: 'sqlserver', imgSrc: SQLServer, title: 'SQL Server' }
]

/** What the picker hands the steps. */
export type TSelectedSource = {
  /** Only set for databases, and fed straight into the auth params. */
  databaseType?: string
  imgSrc?: string
  source: Source
  title: string
}

export enum ESourceStep {
  AUTH = 'auth',
  CUSTOM_AUTH = 'customAuth',
  NAME = 'name'
}

export const getStepMeta = (
  step: ESourceStep
): { title: string; description: string } => {
  switch (step) {
  case ESourceStep.AUTH:
  case ESourceStep.CUSTOM_AUTH:
    return {
      description: 'Fyll i uppgifterna för att ansluta till källan.',
      title: 'Autentisering'
    }
  case ESourceStep.NAME:
    return {
      description:
          'Namnet är bara till för dig och påverkar inte datakällans funktion.',
      title: 'Namnge datakälla'
    }
  }
}

/**
 * The steps a source needs. An API with neither an auth type nor auth params
 * has nothing to fill in, and a file source is configured after it exists, so
 * both go straight to naming.
 */
export const buildSteps = (
  category: ESourceCategory,
  selected: TSelectedSource | null
): ESourceStep[] => {
  if (!selected) {
    return []
  }

  if (category === ESourceCategory.FILE) {
    return [ESourceStep.NAME]
  }

  const { attributes } = selected.source

  if (attributes.is_custom_api) {
    return [ESourceStep.CUSTOM_AUTH, ESourceStep.NAME]
  }

  const hasAuthParams = Boolean(selected.source.included?.auth_params?.length)
  const skipAuth =
    attributes.type === 'API' && !attributes.auth_type && !hasAuthParams

  return skipAuth ? [ESourceStep.NAME] : [ESourceStep.AUTH, ESourceStep.NAME]
}

type TAuthParamValue = {
  attributes: {
    auth_param_id: string | null
    value: string
  }
}

type TValidateBody = {
  data: {
    auth_param_values: TAuthParamValue[]
    source_id: string
  }
}

export const formatValidateBody = (
  sourceId: string,
  attributes: Record<string, string>,
  authParams: AuthParam[],
  excludeOptional = true
): TValidateBody => {
  const data = Object.entries(attributes)
    .filter(([key]) => {
      if (!excludeOptional) return true

      const authParam = authParams.find((item) => item.attributes.field === key)

      return !(authParam && authParam.attributes.optional)
    })
    .map(([key, value]) => {
      const authParam = authParams.find((item) => item.attributes.field === key)

      return {
        attributes: {
          auth_param_id: authParam ? authParam.id : null,
          value
        }
      }
    })

  return {
    data: {
      auth_param_values: data,
      source_id: sourceId
    }
  }
}

type TCreateBody = {
  data: {
    attributes: TValidateBody['data'] & {
      display_name: string
    }
  }
}

export const formatCreateBody = (
  sourceId: string,
  authParamValues: Record<string, string>,
  authParams: AuthParam[],
  displayName: string
): TCreateBody => {
  return {
    data: {
      attributes: {
        ...formatValidateBody(sourceId, authParamValues, authParams, false)
          .data,
        display_name: displayName
      }
    }
  }
}
